import { useUserStore } from '@/stores/userStore';
import { isBrandUserProfile, isComplianceOfficerProfile, isTeamUserProfile } from '@/types/users';
import { getRoleStr, type ApiV3 } from '@teamworksdev/influencer-core';
import { initialize, type LDClient } from 'launchdarkly-js-client-sdk';
import { isEqual } from 'lodash-es';
import type { LDPluginOptions } from 'launchdarkly-vue-client-sdk';

export const ldConfig: LDPluginOptions = {
	context: {
		anonymous: true,
	},
	clientSideID: import.meta.env.VITE_LD_CLIENT_ID,
	options: {
		sendEventsOnlyForVariation: true,
	},
};

export interface LDUserContext {
	kind: 'multi';
	user: {
		key: string;
		name?: string;
		email?: string;
	};
	profile: {
		key: string;
		profileType: 'compliance' | 'team' | 'brand' | 'unknown';
		name: string;
	};
	organization?: {
		key: string;
		name?: string;
		type: 'school' | 'team' | 'brand';
		[attribute: string]: any;
	};
}

function getProfileType(profile: ApiV3.UserProfile): 'team' | 'compliance' | 'brand' | 'unknown' {
	if (isComplianceOfficerProfile(profile)) {
		return 'compliance';
	} else if (isTeamUserProfile(profile)) {
		return 'team';
	} else if (isBrandUserProfile(profile)) {
		return 'brand';
	}
	return 'unknown';
}

export function buildUserContext(user: ApiV3.User, profile: ApiV3.UserProfile): LDUserContext {
	const context: LDUserContext = {
		kind: 'multi',
		user: {
			key: user.uuid,
			email: user.email,
			name: `${user.first_name} ${user.last_name}`,
		},
		profile: {
			key: profile.uuid,
			profileType: getProfileType(profile),
			name: getRoleStr(profile.role_id),
		},
	};

	// Add organization context based on profile type
	if (isComplianceOfficerProfile(profile)) {
		context.organization = {
			key: profile.school.uuid,
			name: profile.school.name,
			schoolId: profile.school.uuid,
			schoolName: profile.school.name,
			type: 'school',
		};
	} else if (isTeamUserProfile(profile)) {
		// Use team as primary organization, but include school info
		context.organization = {
			key: profile.academic_profile.team.uuid || profile.academic_profile.school.uuid,
			name: profile.academic_profile.team.name || profile.academic_profile.school.name,
			type: profile.academic_profile.team.uuid ? 'team' : 'school',
			schoolId: profile.academic_profile.school.uuid,
			schoolName: profile.academic_profile.school.name,
		};
	} else if (isBrandUserProfile(profile)) {
		context.organization = {
			key: profile.exchange_brand.uuid,
			name: profile.exchange_brand.name,
			type: 'brand',
		};
	}

	return context;
}

let client: LDClient | undefined = undefined;
let context: LDUserContext | undefined = undefined;

export async function updateLDContext() {
	const userStore = useUserStore();

	if (!userStore.user) {
		console.warn('[LaunchDarkly][getLDClient] User context missing');
		return;
	}

	if (!userStore.profile) {
		console.warn('[LaunchDarkly][getLDClient] Profile context missing');
		return;
	}

	const newContext = buildUserContext(userStore.user, userStore.profile);

	if (client && isEqual(context, newContext)) return client;

	context = newContext;

	if (client) {
		await client.identify(context, undefined, (err) => {
			if (err) {
				console.error('[LaunchDarkly][getLDClient] Error identifying user:', err);
				return;
			}
			return client;
		});
	}

	// Initialize new client
	client = initialize(
		import.meta.env.VITE_LD_CLIENT_ID,
		context,
		{
			sendEventsOnlyForVariation: true,
			streaming: true,
		},
	);
	await client.waitForInitialization(5);

	return client;
}
